import type { MoneyGranularity, MoneyTestFilter, Payment } from '@/api/opsTypes';
import { toCsv } from './csv';

/**
 * The Money dashboard's pure rules: the period presets → whole UTC days → the half-open instants billing reads, the
 * automatic granularity, the previous equal-length window, and the payments CSV. Tested without a server.
 */

export type PeriodPreset = 'today' | '7d' | '30d' | 'this_month' | 'last_month' | 'custom';

export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
];

/** Billing refuses a window longer than this (money/summary: to − from ≤ 400 days). */
export const MAX_WINDOW_DAYS = 400;

/** A window of whole UTC days, both ends inclusive (`YYYY-MM-DD`). */
export interface DayRange {
  from: string;
  to: string;
}

const DAY_MS = 86_400_000;

function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(day: string, days: number): string {
  return utcDay(new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS));
}

const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/**
 * The days a preset covers, in UTC (billing's periods are UTC). "Last 7 days" ends today and includes it; "Last month"
 * is the previous calendar month in full. `custom` takes the typed days, or null while they are not a valid window.
 */
export function presetRange(preset: PeriodPreset, now: Date = new Date(), custom?: Partial<DayRange>): DayRange | null {
  const today = utcDay(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: addDays(today, -6), to: today };
    case '30d':
      return { from: addDays(today, -29), to: today };
    case 'this_month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'last_month': {
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      return { from: utcDay(first), to: utcDay(last) };
    }
    case 'custom':
      return customRangeProblem(custom?.from ?? '', custom?.to ?? '') ? null : { from: custom!.from!, to: custom!.to! };
  }
}

/** Why a typed custom window cannot be read, in words — or null when it can. */
export function customRangeProblem(from: string, to: string): string | null {
  if (!from || !to) return 'Choose both a first and a last day.';
  if (!isDay(from) || !isDay(to)) return 'Enter days as YYYY-MM-DD.';
  if (from > to) return 'The first day must not be after the last day.';
  if (dayCount({ from, to }) > MAX_WINDOW_DAYS) return `At most ${MAX_WINDOW_DAYS} days at a time.`;
  return null;
}

/** The number of days in an inclusive range. */
export function dayCount(range: DayRange): number {
  return Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / DAY_MS) + 1;
}

/** One point per day up to two months; per month beyond (a 400-day chart of days is a smear). */
export function granularityFor(range: DayRange): MoneyGranularity {
  return dayCount(range) <= 62 ? 'day' : 'month';
}

/** The window of the same length just before this one — the comparison for the headline deltas. */
export function previousRange(range: DayRange): DayRange {
  const days = dayCount(range);
  return { from: addDays(range.from, -days), to: addDays(range.from, -1) };
}

/** The half-open instants `[from 00:00Z, to+1 00:00Z)` billing reads for an inclusive day range. */
export function rangeInstants(range: DayRange): { from: string; to: string } {
  return { from: `${range.from}T00:00:00Z`, to: `${addDays(range.to, 1)}T00:00:00Z` };
}

/**
 * Change against the previous window as a percent, or null when there is nothing to compare with (a previous zero is
 * "new", not +∞ %).
 */
export function deltaPercent(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** `+12.5% vs previous 7 days`, `−3% …`, or null. */
export function deltaText(current: number, previous: number | null | undefined, days: number): string | null {
  const delta = deltaPercent(current, previous);
  if (delta === null) return null;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  const size = Math.abs(delta);
  return `${sign}${size >= 10 ? Math.round(size) : size.toFixed(1)}% vs the previous ${days} day${days === 1 ? '' : 's'}`;
}

/** The test-payments choice → billing's `test` query value (summary) and `test_mode` filter (lists). */
export function testModeFilter(test: MoneyTestFilter): boolean | undefined {
  return test === 'only' ? true : test === 'exclude' ? false : undefined;
}

/** Wallet keys in words. */
export function walletLabel(wallet: string): string {
  if (wallet === 'ai') return 'AI';
  if (wallet === 'communications') return 'SMS & WhatsApp';
  return wallet;
}

/** Minor units → major units for a chart axis (the exact figure stays in the table twin). */
export function minorToMajor(minor: number, digits: number): number {
  return minor / 10 ** digits;
}

/** The payments on screen (or the whole period's) as CSV — billing's own figures, minor units and the currency beside them. */
export function paymentsCsv(rows: readonly Payment[]): string {
  const header = [
    'created_at', 'settled_at', 'payment_id', 'tenant_id', 'tenant_name', 'purpose', 'status', 'amount_minor',
    'refunded_minor', 'currency', 'gateway_key', 'gateway_row_id', 'test_mode', 'method', 'purchase_id', 'invoice_id',
    'failure_reason',
  ];
  return toCsv([
    header,
    ...rows.map((p) => [
      p.created_at, p.settled_at, p.id, p.tenant_id, p.tenant_name ?? null, p.purpose, p.status, p.amount_minor,
      p.refunded_minor, p.currency, p.gateway_key, p.gateway_row_id, p.test_mode ?? null, p.method, p.purchase_id,
      p.invoice_id, p.failure_reason,
    ]),
  ]);
}

/** Rows "Export all in period" stops at — a bigger export belongs to billing, not to a browser loop. */
export const EXPORT_CAP = 5000;

/**
 * Pages through a keyset list until it ends or `cap` rows are in hand. Sequential (one request in flight), so a long
 * export never floods billing; `onProgress` gets the running count. `truncated` says the cap, not the end, stopped it.
 */
export async function collectPages<T>(
  fetchPage: (cursor: string | null) => Promise<{ rows: T[]; next_cursor: string | null }>,
  cap: number = EXPORT_CAP,
  onProgress?: (count: number) => void,
): Promise<{ rows: T[]; truncated: boolean }> {
  const rows: T[] = [];
  let cursor: string | null = null;
  for (;;) {
    const page = await fetchPage(cursor);
    rows.push(...page.rows);
    onProgress?.(Math.min(rows.length, cap));
    if (rows.length >= cap) return { rows: rows.slice(0, cap), truncated: rows.length > cap || page.next_cursor !== null };
    if (!page.next_cursor || page.rows.length === 0) return { rows, truncated: false };
    cursor = page.next_cursor;
  }
}
